import { useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import { supabase } from "../supabase";
import Sidebar from "./Sidebar";
import AnnoncesModal from "./AnnoncesModal";
import { doitRechargerAuRetour } from "../chargement";
import { COLONNES_MESSAGES_LEGERES, filtreNonLus, normaliserMessages, messagesApresRechargement, remplacerParComplets } from "../messages";
import { lireMemoire, ecrireMemoire, allegerPourMemoire } from "../memoire";

export default function LayoutPrincipal({ session, profil, onProfilUpdate }) {
  // Démarre avec la liste de la dernière visite (copie légère), remplacée dès
  // que la vraie arrive (commandesAJour passe à true).
  const [commandes, setCommandes] = useState(() => lireMemoire(profil.id)?.commandes || []);
  const [commandesAJour, setCommandesAJour] = useState(false);
  const [sousComptes, setSousComptes] = useState([]);
  // Utilisateurs supervisables par l'admin (filtre "par utilisateur" dans Commandes).
  // Compatible avec un futur système de compte parent (children via master_id).
  const [utilisateursSupervises, setUtilisateursSupervises] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  // Anti-concurrence + horodatage du dernier chargement réussi (throttle focus).
  const chargementRef = useRef(false);
  const dernierChargementRef = useRef(0);
  const reseauRevenuRef = useRef(false); // "online" reçu onglet caché : recharger au prochain retour
  // Dernière commande dont le fil complet a été demandé (celle qu'on regarde).
  // Au rechargement, seul son fil est gardé et rechargé ; les autres fils
  // redeviennent légers et seront rechargés à leur prochaine ouverture.
  const ouverteRef = useRef(null);
  // Numéro de « tournée » : augmente à chaque (re)démarrage du grand effet et à
  // sa fin (déconnexion). Un chargement parti avec un autre numéro est jeté :
  // ni liste, ni copie, ni « à jour ».
  const genRef = useRef(0);

  const isAdmin = profil.role !== "dessinateur" && profil.is_owner === true;

  // auteurNom used in realtime handler and normalizations
  const auteurNom = `${profil.prenom ?? ""} ${profil.nom ?? ""}`.trim();
  // Keep a ref so the realtime handler always reads the current value
  const auteurNomRef = useRef(auteurNom);
  auteurNomRef.current = auteurNom;

  // Visibilité d'un message selon le rôle (notes privées historiques + portée).
  // Ref pour rester accessible dans le handler realtime.
  const peutVoirMessageRef = useRef();
  peutVoirMessageRef.current = (m, nom) => {
    if (m.auteur === nom) return true; // l'auteur voit toujours son propre message
    if (m.visible_par && !m.visible_par.includes(nom)) return false;
    // /note : visible par l'auteur (ci-dessus) + l'admin (ou parent, à venir)
    const estAdmin = profil.role !== "dessinateur" && profil.is_owner === true;
    if ((m.portee || "public") === "note" && !estAdmin) return false;
    return true;
  };

  // Nombre de commandes actives avec notification (messages non lus OU marquage manuel)
  // — même décompte que la pastille a cote du titre de la page Commandes.
  const role = profil.role;
  // Notifications uniquement sur SES propres commandes (proprietaire ou
  // dessinateur assigne) — pas en supervision admin.
  const estMaCommande = (c) => c.utilisateur_id === profil.id || c.dessinateur_id === profil.id;
  const totalNonLus = commandes.filter(c => {
    if (!estMaCommande(c)) return false;
    const archived = role === "dessinateur" ? c.is_archived_dessinateur : c.is_archived;
    if (archived) return false;
    const nbNonLus = (c.messages || []).filter(
      m => m.auteur !== auteurNom && !(m.lu_par || []).includes(auteurNom)
    ).length;
    return nbNonLus > 0 || c.marque_non_lu;
  }).length;

  // Nombre de tickets avec au moins un message non lu (badge sidebar).
  // Un message est non lu s'il vient d'un autre auteur et que mon id n'est
  // pas dans lu_par. Calque du décompte des notifications de commandes.
  const uid = session?.user?.id;
  const ticketsNonLus = tickets.filter(t =>
    (t.messages || []).some(m => m.auteur_id !== uid && !(m.lu_par || []).includes(uid))
  ).length;

  useEffect(() => {
    genRef.current += 1;
    chargementRef.current = false; // un chargement de l'ancienne tournée ne bloque pas celui-ci
    chargerToutAvecReprise();

    // Recharge quand l'onglet redevient visible / au focus / retour réseau :
    // le token peut avoir expiré en arrière-plan et laisser un état vide.
    const recharger = (e) => {
      // Throttle : au retour sur l'onglet, on ne recharge que si ≥ 5 min (toujours au retour du réseau) depuis
      // le dernier chargement réussi (évite de refaire le gros payload trop souvent).
      if (e?.type === "online") reseauRevenuRef.current = true;
      if (document.visibilityState !== "visible") return;
      const type = reseauRevenuRef.current ? "online" : e?.type;
      if (!doitRechargerAuRetour(dernierChargementRef.current, Date.now(), type)) return;
      reseauRevenuRef.current = false;
      chargerToutAvecReprise();
    };
    document.addEventListener("visibilitychange", recharger);
    window.addEventListener("focus", recharger);
    window.addEventListener("online", recharger);

    // Realtime channel — reproduit le canal de VueUtilisateur.js:68-101
    // et VueDessinateur.js:47-81 (patterns identiques, seul le nom du canal diffère).
    const canal = supabase
      .channel("messages-layout")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages" }, (payload) => {
        const msg = payload.new;
        const nom = auteurNomRef.current;
        // Ne pas stocker les messages non visibles pour ce rôle (notes privées / portée)
        if (!peutVoirMessageRef.current(msg, nom)) return;
        setCommandes(prev => prev.map(c => {
          if (c.id !== msg.commande_id) return c;
          if (msg.auteur === nom) return c;
          if (c.messages.some(m => m.id === msg.id)) return c;
          return { ...c, messages: [...c.messages, msg] };
        }));
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "messages" }, (payload) => {
        const msg = payload.new;
        setCommandes(prev => prev.map(c =>
          c.id === msg.commande_id
            ? { ...c, messages: c.messages.map(m => m.id === msg.id ? { ...m, lu_par: msg.lu_par } : m) }
            : c
        ));
      })
      .subscribe();

    // Canal tickets — INSERT d'un message met à jour le décompte des non-lus.
    const canalTickets = supabase
      .channel("ticket-messages-layout")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "ticket_messages" }, (payload) => {
        const m = payload.new;
        setTickets(prev => prev.map(t => {
          if (t.id !== m.ticket_id) return t;
          if ((t.messages || []).some(x => x.id === m.id)) return t;
          return { ...t, messages: [...(t.messages || []), { id: m.id, auteur_id: m.auteur_id, lu_par: m.lu_par || [] }] };
        }));
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "ticket_messages" }, (payload) => {
        const m = payload.new;
        setTickets(prev => prev.map(t =>
          t.id === m.ticket_id
            ? { ...t, messages: (t.messages || []).map(x => x.id === m.id ? { ...x, lu_par: m.lu_par || [] } : x) }
            : t
        ));
      })
      .subscribe();

    return () => {
      genRef.current += 1;
      supabase.removeChannel(canal);
      supabase.removeChannel(canalTickets);
      document.removeEventListener("visibilitychange", recharger);
      window.removeEventListener("focus", recharger);
      window.removeEventListener("online", recharger);
    };
  }, [profil.id, auteurNom, profil.role, profil.is_owner]); // eslint-disable-line

  // Chargement avec reprise : rejoue en cas d'échec transitoire (payload lourd,
  // token expiré...) au lieu de laisser une liste vide définitive.
  async function chargerToutAvecReprise() {
    const gen = genRef.current; // tournée de ce chargement : une tournée périmée ne touche plus aux drapeaux
    if (chargementRef.current) return;
    chargementRef.current = true;
    try {
      for (let i = 0; i < 4; i++) {
        try { await chargerTout(); if (gen === genRef.current) dernierChargementRef.current = Date.now(); return; }
        catch (e) {
          console.warn("chargerTout: échec, tentative", i + 1, e);
          // Après le 1er échec, tente de rafraîchir la session (token périmé).
          if (i === 0) { try { await supabase.auth.refreshSession(); } catch { /* ignore */ } }
          await new Promise(r => setTimeout(r, 500 * (i + 1)));
        }
      }
      console.error("chargerTout: échec après plusieurs tentatives");
    } finally {
      if (gen === genRef.current) chargementRef.current = false;
    }
  }

  // Commandes + leurs seuls messages non lus par moi (légers). Les filtres
  // portent sur la table jointe : ils trient les messages, jamais les commandes.
  function requeteCommandes(nom) {
    let q = supabase.from("commandes").select(`*, messages(${COLONNES_MESSAGES_LEGERES})`);
    if (nom) q = q.neq("messages.auteur", nom);
    const filtre = filtreNonLus(nom);
    if (filtre) q = q.or(filtre, { referencedTable: "messages" });
    return q.order("created_at", { ascending: false });
  }

  // Fil complet d'une commande (texte, fichiers). 3 essais espacés, car la
  // fenêtre de détail attend ce fil pour afficher la messagerie. Échec final :
  // messagesErreur = true, la messagerie montre un bouton pour recharger.
  async function chargerMessagesComplets(commandeId) {
    const nom = auteurNomRef.current;
    ouverteRef.current = commandeId;
    for (let essai = 0; essai < 3; essai++) {
      if (essai > 0) await new Promise(r => setTimeout(r, 1000 * essai));
      const debut = Date.now();
      const res = await Promise.resolve(
        supabase.from("messages").select("*").eq("commande_id", commandeId)
      ).catch(e => ({ data: null, error: e }));
      if (res.error || !res.data) continue;
      const complets = normaliserMessages(res.data, m => peutVoirMessageRef.current(m, nom));
      setCommandes(prev => prev.map(c =>
        c.id === commandeId
          ? { ...c, messagesComplets: true, messagesErreur: false, messages: remplacerParComplets(c.messages, complets, debut) }
          : c
      ));
      return true;
    }
    // Un autre chargement a pu réussir entre-temps : pas d'erreur sur un fil déjà complet.
    setCommandes(prev => prev.map(c => c.id === commandeId ? { ...c, messagesErreur: !c.messagesComplets } : c));
    return false;
  }

  async function chargerTout() {
    const gen = genRef.current;
    const nom = auteurNomRef.current;

    // Chaque requête ne rejette jamais (erreur réseau -> {data:null, error}) :
    // ainsi une requête secondaire en échec ne fait pas tomber tout le chargement.
    const [cmdRes, subRes, marquesRes, tksRes, supervRes] = await Promise.all([
      requeteCommandes(nom),
      profil.is_owner
        ? supabase.from("profiles").select("id, prenom, nom").eq("master_id", profil.id)
        : Promise.resolve({ data: [] }),
      supabase.from("commande_marquage_non_lu").select("commande_id"),
      supabase.from("tickets").select("id, statut, ticket_messages(id, auteur_id, lu_par)").order("updated_at", { ascending: false }),
      isAdmin
        ? supabase.from("profiles").select("id, prenom, nom").eq("role", "utilisateur").neq("id", profil.id)
        : Promise.resolve({ data: [] }),
    ].map(p => Promise.resolve(p).catch(e => ({ data: null, error: e }))));
    if (gen !== genRef.current) return; // tournée périmée (profil changé, déconnexion) : on jette

    // Commandes = requête critique. En cas d'erreur (réseau, token, timeout),
    // on remonte pour laisser la reprise rejouer, SANS écraser l'existant.
    if (cmdRes.error || cmdRes.data == null) {
      throw cmdRes.error || new Error("Chargement des commandes échoué");
    }
    const cmd = cmdRes.data;
    const marquesSet = new Set((marquesRes.data || []).map(m => m.commande_id));
    setCommandes(prev => {
      const precParId = new Map(prev.map(c => [c.id, c]));
      return cmd.map(c => {
        const prec = precParId.get(c.id);
        const arrivants = normaliserMessages(c.messages, m => peutVoirMessageRef.current(m, nom));
        return {
          ...c,
          plans: c.plans || [],
          fichiersPlan: c.fichiers_plan || [],
          logoClient: c.logo_client || [],
          plansFinalises: c.plans_finalises || [],
          marque_non_lu: marquesSet.has(c.id),
          messagesComplets: c.id === ouverteRef.current && !!prec?.messagesComplets,
          messages: messagesApresRechargement(prec, arrivants, c.id === ouverteRef.current),
        };
      });
    });
    // Le fil de la commande regardée est remis à jour (messages arrivés ou supprimés entre-temps).
    if (ouverteRef.current) chargerMessagesComplets(ouverteRef.current);
    setCommandesAJour(true);
    // Copie pour la prochaine ouverture : données reçues, légères, non lues seulement.
    ecrireMemoire(profil.id, {
      commandes: allegerPourMemoire(cmd.map(c => ({
        ...c,
        plans: c.plans || [],
        fichiersPlan: c.fichiers_plan || [],
        logoClient: c.logo_client || [],
        plansFinalises: c.plans_finalises || [],
        marque_non_lu: marquesSet.has(c.id),
        messages: normaliserMessages(c.messages, m => peutVoirMessageRef.current(m, nom)),
      })), nom),
    });

    if (subRes.data) setSousComptes(subRes.data);
    setUtilisateursSupervises(supervRes.data || []);
    setTickets((tksRes.data || []).map(t => ({ ...t, messages: t.ticket_messages || [] })));
  }

  return (
    <div
      onClick={() => { showMobileMenu && setShowMobileMenu(false); }}
      style={{ display: "flex", height: "100dvh", fontFamily: "'Segoe UI', system-ui, sans-serif", background: "#F5FAFF", color: "#111827" }}
    >
      {/* Annonces broadcast — modale affichée à la connexion */}
      <AnnoncesModal profil={profil} />

      {/* Backdrop mobile — reproduit de VueUtilisateur.js:453 et VueDessinateur.js:291 */}
      <div
        className={`sidebar-backdrop${showMobileMenu ? " sidebar-open" : ""}`}
        onClick={(e) => { e.stopPropagation(); setShowMobileMenu(false); }}
      />

      {/* Sidebar */}
      <Sidebar
        session={session}
        profil={profil}
        totalNonLus={totalNonLus}
        ticketsNonLus={ticketsNonLus}
        showMobileMenu={showMobileMenu}
        onCloseMobile={() => setShowMobileMenu(false)}
      />

      {/* Main wrapper — reproduit de VueUtilisateur.js:500-513 et VueDessinateur.js:336-348 */}
      <div
        className="app-main-wrapper"
        style={{ marginLeft: 220, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        {/* Mobile header */}
        <div className="mobile-header">
          <button
            onClick={(e) => { e.stopPropagation(); setShowMobileMenu(v => !v); }}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: "#374151", padding: "4px 8px 4px 0", lineHeight: 1 }}
          >
            ☰
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, background: profil.role === "dessinateur" ? "#FC6C1B" : "#122131", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ color: "white", fontSize: 14 }}>{profil.role === "dessinateur" ? "✏️" : "🔥"}</span>
            </div>
            <span style={{ fontWeight: 700, fontSize: 13 }}>Incendie Plan</span>
          </div>
          {totalNonLus > 0 && (
            <span style={{ marginLeft: "auto", background: "#FC6C1B", color: "#fff", borderRadius: 10, padding: "2px 7px", fontSize: 11, fontWeight: 700 }}>
              {totalNonLus}
            </span>
          )}
        </div>

        {/* Contenu — pages enfants via Outlet */}
        <div
          className="app-main-content"
          style={{ flex: 1, padding: "32px 32px", overflowY: "auto" }}
        >
          <Outlet
            context={{
              session,
              profil,
              onProfilUpdate,
              commandes,
              commandesAJour,
              setCommandes,
              chargerMessagesComplets,
              sousComptes,
              utilisateursSupervises,
              tickets,
              setTickets,
            }}
          />
        </div>
      </div>
    </div>
  );
}
